import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { UserSearchSupportDto } from '@api/MobileClient';
import { Combobox } from '@components/combobox';
import { injectTSDI } from '@core/tsdi';
import { Api } from '@core/api';

export interface PostAuthor {
    userId: string;
    name: string;
}

export interface UserSelectProps {
    value: PostAuthor | null;
    label?: string;
    onChange: (value: PostAuthor | null) => void;
    error?: string;
    disabled?: boolean;
    restoreKey?: boolean;
}

const STORAGE_KEY = 'feedPlanning.postAs';
const PAGE_SIZE = 30;

function mainEmail(user: UserSearchSupportDto): string | undefined {
    return (
        user.emails?.find((entry) => entry.main)?.value ??
        user.emails?.[0]?.value
    );
}

function userDisplayName(user: UserSearchSupportDto): string {
    const name = [user.firstName, user.lastName]
        .filter((part) => part && part.trim())
        .join(' ')
        .trim();
    return name || mainEmail(user) || user.profileId;
}

function userInitials(user: UserSearchSupportDto): string {
    const first = user.firstName?.trim();
    const last = user.lastName?.trim();
    if (first || last) {
        return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase();
    }
    return (mainEmail(user)?.[0] ?? '?').toUpperCase();
}

function userSubtitle(user: UserSearchSupportDto): string {
    const email = mainEmail(user);
    if (email && email !== userDisplayName(user)) {
        return `#${user.id} · ${email}`;
    }
    return `#${user.id}`;
}

function toAuthor(user: UserSearchSupportDto): PostAuthor {
    return { userId: String(user.id), name: userDisplayName(user) };
}

function toOption(author: PostAuthor): UserSearchSupportDto {
    return {
        id: Number(author.userId) || 0,
        emails: [],
        profileId: '',
        firstName: author.name,
        lastName: '',
        userType: 'USER',
        feedId: ''
    };
}

export const UserSelect: React.FC<UserSelectProps> = ({
    value,
    label = 'Posting as',
    onChange,
    error,
    disabled,
    restoreKey
}) => {
    const { mobileClient } = injectTSDI(Api);
    const [searchTerm, setSearchTerm] = useState('');
    const [options, setOptions] = useState<UserSearchSupportDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState<UserSearchSupportDto | null>(
        value ? toOption(value) : null
    );
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    const fetchPage = useCallback(
        async (nextPage: number, search: string) => {
            const response =
                await mobileClient.supportApi.userSearchSupportControllerSearchUsers(
                    {
                        search: search || undefined,
                        page: nextPage,
                        size: PAGE_SIZE,
                        searchMode: 'EVERYONE'
                    }
                );
            const data = response.data;
            setHasMore(data.number < data.totalPages - 1);
            return data.content;
        },
        [mobileClient]
    );

    useEffect(() => {
        let active = true;
        const timerId = setTimeout(async () => {
            setLoading(true);
            try {
                const content = await fetchPage(0, searchTerm);
                if (active) {
                    setOptions(content);
                    setPage(0);
                }
            } catch (err) {
                console.error('Error fetching users:', err);
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        }, 300);
        return () => {
            active = false;
            clearTimeout(timerId);
        };
    }, [searchTerm, fetchPage]);

    const loadMore = useCallback(async () => {
        if (loading || !hasMore) {
            return;
        }
        setLoading(true);
        try {
            const nextPage = page + 1;
            const content = await fetchPage(nextPage, searchTerm);
            setOptions((prev) => {
                const seen = new Set(prev.map((user) => user.profileId));
                return [
                    ...prev,
                    ...content.filter((user) => !seen.has(user.profileId))
                ];
            });
            setPage(nextPage);
        } catch (err) {
            console.error('Error fetching users:', err);
        } finally {
            setLoading(false);
        }
    }, [loading, hasMore, page, searchTerm, fetchPage]);

    useEffect(() => {
        if (!value) {
            return;
        }
        if (String(selected?.id) === value.userId) {
            return;
        }
        const fromOptions = options.find((u) => String(u.id) === value.userId);
        setSelected(fromOptions ?? toOption(value));
    }, [value, options]);

    useEffect(() => {
        if (!restoreKey || value) {
            return;
        }
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) {
            return;
        }
        let parsed: PostAuthor;
        try {
            parsed = JSON.parse(stored);
        } catch {
            return;
        }
        if (!parsed?.userId || !parsed?.name) {
            return;
        }
        onChange(parsed);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [restoreKey]);

    const mergedOptions = useMemo(() => {
        if (selected && !options.some((o) => o.id === selected.id)) {
            return [selected, ...options];
        }
        return options;
    }, [selected, options]);

    return (
        <Combobox<UserSearchSupportDto>
            label={label}
            placeholder="Select a user..."
            searchPlaceholder="Search users..."
            options={mergedOptions}
            getOptionLabel={userDisplayName}
            getOptionKey={(option) => option.profileId || String(option.id)}
            value={selected}
            disabled={disabled}
            error={error}
            loading={loading}
            onInputChange={setSearchTerm}
            onScrollEnd={() => void loadMore()}
            onChange={(newValue) => {
                setSelected(newValue);
                if (newValue) {
                    const author = toAuthor(newValue);
                    if (restoreKey !== undefined) {
                        localStorage.setItem(
                            STORAGE_KEY,
                            JSON.stringify(author)
                        );
                    }
                    onChange(author);
                } else {
                    onChange(null);
                }
            }}
            renderOption={(option) => (
                <div className="flex min-w-0 flex-1 items-center gap-3 py-0.5">
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium">
                        {userInitials(option)}
                    </span>
                    <span className="min-w-0">
                        <span className="block truncate text-sm font-bold">
                            {userDisplayName(option)}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">
                            {userSubtitle(option)}
                        </span>
                    </span>
                </div>
            )}
        />
    );
};
